import type { Map } from 'mapbox-gl'
import { MapAdapter, MapAdapterOptions } from './types'
import { Location, GeoPoint } from '../SearchLocation.types'
import {
  parsePointString,
  getZoomLevelForLocationType,
} from '../utils/geoUtils'

/**
 * Mapbox adapter - implements MapAdapter interface for Mapbox GL JS
 */
export class MapboxAdapter implements MapAdapter {
  private map: Map | null
  private mapLoaded: boolean
  private options?: MapAdapterOptions

  constructor(
    map: Map | null,
    mapLoaded: boolean,
    options?: MapAdapterOptions
  ) {
    this.map = map
    this.mapLoaded = mapLoaded
    this.options = options
  }

  /**
   * Navigate the Mapbox map to a location's coordinates
   * Parses geo_center and flies to the location with appropriate zoom level
   * Calls onMarkerPlace callback after navigation completes if provided
   */
  navigateToLocation(location: Location): void {
    if (!this.isAvailable() || !location?.geo_center) {
      return
    }

    // Parse geo_center - can be either a string or a GeoPoint object
    let geoPoint: GeoPoint | null = null

    if (typeof location.geo_center === 'string') {
      geoPoint = parsePointString(location.geo_center)
    } else if (location.geo_center.lat && location.geo_center.lon) {
      geoPoint = location.geo_center
    }

    if (!geoPoint) {
      return
    }

    // Get appropriate zoom level for location type
    const zoom = getZoomLevelForLocationType(location.location_type)

    // Fly to the location
    if (this.map) {
      // If onMarkerPlace callback is provided, wait for flyTo to complete
      if (this.options?.onMarkerPlace) {
        const handleMoveEnd = () => {
          this.options?.onMarkerPlace?.(location)
          this.map?.off('moveend', handleMoveEnd)
        }
        this.map.once('moveend', handleMoveEnd)
      }

      this.map.flyTo({
        center: [geoPoint.lon, geoPoint.lat],
        zoom,
        duration: 1500,
        essential: true,
      })
    }
  }

  /**
   * Check if Mapbox map is available and ready to use
   */
  isAvailable(): boolean {
    return this.map !== null && this.mapLoaded
  }

  /**
   * Get the underlying Mapbox map instance
   */
  getMapInstance(): Map | null {
    return this.map
  }
}
